/**
 * Sync Service - Offline-first sync manager
 * Queues writes when offline, syncs when online
 * Per spec Section 9 (Offline Strategy)
 */

const QUEUE_STORAGE_KEY = 'crm_poker_sync_queue'
const LAST_SYNC_KEY = 'crm_poker_last_sync'

/**
 * Actions that can be queued offline
 */
export const QueueableAction = {
  LOG_CONTACT: 'log_contact',
  LOG_PROGRESS: 'log_progress',
  LOG_COLLECTION: 'log_collection',
  COMPLETE_TASK: 'complete_task',
  COMPLETE_EVENT_TASK: 'complete_event_task',
  CREATE_CLIENT: 'create_client',
  CREATE_TASK: 'create_task',
  PATCH_CLIENT: 'patch_client',
  PATCH_LOAN: 'patch_loan',
  SESSION_RESPONSE: 'session_response',
  SESSION_ATTENDANCE: 'session_attendance',
}

/**
 * Actions that are DISABLED offline (require real-time confirmation)
 */
export const DisabledOffline = {
  CLAIM_TASK: 'claim_task',
  CLAIM_EVENT_TASK: 'claim_event_task',
  DELETE_ANY: 'delete',
}

/**
 * Sync Manager - handles offline queue and sync
 */
class SyncManager {
  constructor() {
    this.queue = this.loadQueue()
    this.conflicts = []
    this.isOnline = navigator.onLine
    this.listeners = new Set()

    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true
      this.notifyListeners()
      this.attemptSync()
    })

    window.addEventListener('offline', () => {
      this.isOnline = false
      this.notifyListeners()
    })
  }

  /**
   * Load queue from localStorage
   */
  loadQueue() {
    try {
      const stored = localStorage.getItem(QUEUE_STORAGE_KEY)
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  }

  /**
   * Save queue to localStorage
   */
  saveQueue() {
    try {
      localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(this.queue))
    } catch (error) {
      console.error('Failed to save sync queue:', error)
    }
  }

  /**
   * Get last sync timestamp
   */
  getLastSync() {
    return localStorage.getItem(LAST_SYNC_KEY)
  }

  /**
   * Update last sync timestamp
   */
  updateLastSync() {
    localStorage.setItem(LAST_SYNC_KEY, new Date().toISOString())
  }

  /**
   * Generate unique queue item ID
   */
  generateId() {
    return `queue-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Queue an action for sync
   * @param {Object} action - Action to queue
   * @returns {string} Queue item ID
   */
  queueAction(action) {
    const queueItem = {
      id: this.generateId(),
      ...action,
      queued_at: new Date().toISOString(),
    }

    this.queue.push(queueItem)
    this.saveQueue()
    this.notifyListeners()

    return queueItem.id
  }

  /**
   * Remove item from queue
   * @param {string} id - Queue item ID
   */
  removeFromQueue(id) {
    this.queue = this.queue.filter((item) => item.id !== id)
    this.saveQueue()
    this.notifyListeners()
  }

  /**
   * Clear entire queue
   */
  clearQueue() {
    this.queue = []
    this.saveQueue()
    this.notifyListeners()
  }

  /**
   * Get current queue count
   */
  getQueueCount() {
    return this.queue.length
  }

  /**
   * Get all queued items
   */
  getQueue() {
    return [...this.queue]
  }

  /**
   * Check if an action can be queued (vs disabled offline)
   * @param {string} actionType - Action type
   * @returns {boolean}
   */
  canQueueAction(actionType) {
    return Object.values(QueueableAction).includes(actionType)
  }

  /**
   * Check if an action is disabled offline
   * @param {string} actionType - Action type
   * @returns {boolean}
   */
  isDisabledOffline(actionType) {
    return Object.values(DisabledOffline).some((disabled) =>
      actionType.toLowerCase().includes(disabled)
    )
  }

  /**
   * Attempt to sync queued actions
   * @param {Function} sendAction - Function to send action to server
   * @returns {Object} Sync results
   */
  async attemptSync(sendAction) {
    if (!this.isOnline || this.queue.length === 0) {
      return { synced: 0, conflicts: 0, errors: 0 }
    }

    const results = { synced: 0, conflicts: 0, errors: 0 }
    const toRemove = []

    for (const item of this.queue) {
      try {
        const response = await sendAction(item)

        if (response?.conflict) {
          // Conflict detected - add to conflicts for user resolution
          this.conflicts.push({
            item,
            conflict: response.conflict,
          })
          results.conflicts++
        } else {
          // Success - mark for removal
          toRemove.push(item.id)
          results.synced++
        }
      } catch (error) {
        // Network error or server error
        console.error('Sync failed for item:', item.id, error)
        results.errors++

        // Stop syncing on network error (will retry when online again)
        if (!navigator.onLine) {
          break
        }
      }
    }

    // Remove successfully synced items
    toRemove.forEach((id) => this.removeFromQueue(id))

    if (results.synced > 0) {
      this.updateLastSync()
    }

    this.notifyListeners()

    return results
  }

  /**
   * Get pending conflicts
   */
  getConflicts() {
    return [...this.conflicts]
  }

  /**
   * Resolve a conflict
   * @param {string} itemId - Queue item ID
   * @param {string} resolution - 'keep_mine' or 'keep_theirs'
   */
  resolveConflict(itemId, resolution) {
    const index = this.conflicts.findIndex((c) => c.item.id === itemId)
    if (index === -1) return

    const conflict = this.conflicts[index]
    this.conflicts.splice(index, 1)

    if (resolution === 'keep_mine') {
      // Retry with force flag
      conflict.item.force = true
      // Item stays in queue for next sync
    } else {
      // Keep theirs - remove from queue
      this.removeFromQueue(itemId)
    }

    this.notifyListeners()
  }

  /**
   * Subscribe to sync state changes
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.listeners.add(callback)
    // Immediately call with current state
    callback(this.getState())

    return () => {
      this.listeners.delete(callback)
    }
  }

  /**
   * Notify all listeners of state change
   */
  notifyListeners() {
    const state = this.getState()
    this.listeners.forEach((callback) => {
      try {
        callback(state)
      } catch (error) {
        console.error('Error in sync listener:', error)
      }
    })
  }

  /**
   * Get current sync state
   */
  getState() {
    return {
      isOnline: this.isOnline,
      queueCount: this.queue.length,
      conflictCount: this.conflicts.length,
      lastSync: this.getLastSync(),
    }
  }
}

// Singleton instance
export const syncManager = new SyncManager()

export default syncManager
